import { useEffect } from 'react';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { IncomingCallModalContainer } from './IncomingCallModalContainer';
import { ErrorStateBanner } from './ErrorStateBanner';
import { useAmazonConnectAgent } from '@/hooks/useAmazonConnectAgent';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { FORCED_RELEASE_STATE } from '@/constants/amazonConnect';
import { Box } from '@gapartners/ui';

export function AmazonConnectControls() {
  const {
    agentState,
    isAfterCallWork,
    isErrorState,
    isChangingState,
    isRoutable,
    setIsChangingState,
  } = useAmazonConnectAgent();
  const { pendingFinishContacts } = useAmazonConnectContacts();

  // While a VOICE after-call-work is pending, keep the agent not-routable so
  // Connect won't ring them with a re-offered call until they finish. Chat ACW
  // must NOT force this: chats leave the agent "Available" (routable) and
  // Connect reverts the state, causing an Available↔Forced Release loop that
  // blocks the Finish button.
  const hasPendingVoice = pendingFinishContacts.some(contact => {
    try {
      return !connectAdapter.isChatContact(contact);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const acwPending = isAfterCallWork || hasPendingVoice;
    if (acwPending && isRoutable) {
      connectAdapter.setAgentStateByName(FORCED_RELEASE_STATE, {});
    }
  }, [isAfterCallWork, hasPendingVoice, isRoutable]);

  const setAgentStateWithLoading = (
    state: 'ONLINE' | 'OFFLINE' | 'ROUTABLE' | 'NOT_ROUTABLE'
  ) => {
    setIsChangingState(true);

    // Fallback timeout: if state change callback doesn't fire within 5 seconds, reset loading
    const timeoutId = setTimeout(() => {
      setIsChangingState(false);
    }, 5000);

    connectAdapter.setAgentState(state, {
      success: () => {
        // Some transitions (or no-op transitions) may not emit onStateChange.
        // Always stop the loading spinner on success.
        clearTimeout(timeoutId);
        setIsChangingState(false);
      },
      failure: () => {
        clearTimeout(timeoutId);
        setIsChangingState(false);
      },
    });
  };

  const handleClearErrorState = () => setAgentStateWithLoading('ROUTABLE');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <IncomingCallModalContainer />

      {isErrorState && (
        <ErrorStateBanner
          agentState={agentState}
          onClear={handleClearErrorState}
          isLoading={isChangingState}
        />
      )}
    </Box>
  );
}
